import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../core/base.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HeaderService } from '../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { AuthService } from '../core/security/auth.service';
import { ProMessagingService } from './pro-messaging.service';
import { FormGroup, UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { FileHelpers } from '../core/utilities/file.helpers';
import { NotificationService } from '../notification/notification.service';
import { CurrentUser } from '../api/models/current-user';
import { ModulesEnum } from '../core/security/modules.enum';
import { RolesEnum } from '../core/security/roles.enum';
import {
  BillingCycleLimitModel,
  ContactTagLookupModel,
  MessageModel,
  MessagingGroupModel,
  SendProMessageModel,
  SmsPhoneNumberModel,
  SmsProcessingPhoneNumberModel,
  WhatsAppSendTemplateModel,
} from '../api/models';
import { MatDialog } from '@angular/material/dialog';
import { MatConfirmDialogComponent } from '../dialogs/mat-confirm/mat-confirm-dialog.component';
import { ProPhoneNumberGroupV2Component } from './phone-number-group/pro-phone-number-group-v2.component';
import { MatDrawer, MatDrawerContainer, MatDrawerMode, MatDrawerContent } from '@angular/material/sidenav';
import { LocalNotificationsService } from '../core/services/local-notifications.service';
import { ConfirmConversationDeleteDialogComponent } from './confirm-conversation-delete/confirm-conversation-delete-dialog.component';
import { ZendeskService } from '../core/services/zendesk.service';
import { DateTime } from 'luxon';
import { MatAlertDialogComponent } from '../dialogs/mat-alert/mat-alert-dialog.component';
import { SelectWhatsappTemplateDialogComponent } from '../pro-messaging-whats-app-template/select-template-v2/select-whatsapp-template-dialog.component';
import { ProMessagesDatasource } from './messages-grid/pro-messages.datasource';
import { AddSmsNumberDialogComponent } from '../phone-numbers/add-sms-number-dialog/add-sms-number-dialog.component';
import { PhoneNumbersService } from '../phone-numbers/phone-numbers.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MessagingHelper } from './messaging.helper';
import { NgIf, NgFor, NgClass, NgStyle, AsyncPipe } from '@angular/common';
import { MatCard, MatCardHeader, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton, MatIconAnchor } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFormFieldFix } from '../editors/mat-form-field/mat-form-field-fix';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { ExplanationComponent } from '../core/visual/explanation/explanation.component';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { MatOption } from '@angular/material/core';
import { MatBadge } from '@angular/material/badge';
import { MatInput } from '@angular/material/input';
import { ThreadRecipientsComponent } from './thread-recipients/thread-recipients.component';
import { ProMessagesGridV2Component } from './messages-grid/pro-messages-grid-v2.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { SelectTemplateV2Component } from '../pro-messaging-template/select-template-v2/select-template-v2.component';
import { DroppableDirective } from '../core/visual/droppable/droppable.directive';
import { PhoneNumberPipe } from '../core/pipes/phone-number.pipe';

@Component({
    selector: 'app-pro-messaging-v2',
    templateUrl: './pro-messaging.component.html',
    styleUrls: ['./pro-messaging.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        MatCard,
        MatCardHeader,
        MatCardContent,
        MatIcon,
        MatButton,
        NgFor,
        FlexModule,
        MatProgressSpinner,
        MatDrawerContainer,
        MatDrawer,
        MatIconButton,
        MatFormFieldFix,
        MatFormField,
        MatSelect,
        FormsModule,
        MatSelectTrigger,
        ExplanationComponent,
        MatButtonToggleGroup,
        MatButtonToggle,
        MatOption,
        MatBadge,
        MatPrefix,
        MatInput,
        MatIconAnchor,
        MatSuffix,
        ProPhoneNumberGroupV2Component,
        MatDrawerContent,
        ReactiveFormsModule,
        NgClass,
        ThreadRecipientsComponent,
        ProMessagesGridV2Component,
        MatTooltip,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        NgStyle,
        CdkTextareaAutosize,
        SelectTemplateV2Component,
        DroppableDirective,
        AsyncPipe,
        PhoneNumberPipe,
    ],
})
export class ProMessagingComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly TAG = '[ProMessagingComponent]';

  @ViewChild('mainContainer') mainContainer: MatDrawerContainer;
  @ViewChild('threadCmp') threadCmp: ProPhoneNumberGroupV2Component;
  @ViewChild('msgMenu') msgMenu: ElementRef;
  @ViewChild('textInput') textInput: ElementRef;
  @ViewChild('fileInput') fileInput: any;
  @ViewChild('threadsDrawer') threadsDrawer: MatDrawer;

  searchTextChanged: Subject<string> = new Subject<string>();
  staticMessagesSubscription: Subscription;
  newMessageSubscription: Subscription;
  routedPhoneNumber: string;
  routedThreadKey: string;
  selectedGroup?: MessagingGroupModel;
  selectedTemplate?: WhatsAppSendTemplateModel;
  refreshProcessingNumbersTimeout: any;
  form: FormGroup;
  submitting = false;
  readOnlyPhone = true;
  buttonSubmitPromise: Promise<void>;
  uncompletedMessages: { [number: string]: string | undefined } = {};

  filesToUpload: File[] = [];
  previewUrl?: string;
  previewFileName?: string;

  hasContactsModule = false;
  hasContactsViewRight = false;
  hasContactsEditRight = false;
  hasPhoneNumbersEditRight = false;
  hasAssignUserAccess = false;
  searchTextInternal = '';
  searchText = '';
  hasTemplateAccess = false;
  sendAsGroup = false;
  smsThreadSortType = 'sortByDateAndUnread';

  communicationType: 'text' | 'waba' = 'text';

  private paramsSubscription: Subscription;

  providerTypes: Array<{
    value: 'text' | 'waba';
    title: string;
    class: string;
  }> = [];

  isAdmin = false;
  limitsReached = false;
  messagingLimits: BillingCycleLimitModel = {
    limits: 0,
    currentUsage: 0,
    billingCycleStart: '',
    currentBillingCycleStart: '',
    currentBillingCycleEnd: '',
  };
  lastMessage: MessageModel;

  sidenavMode: MatDrawerMode = 'side';
  showThreads = true;
  isMobileView = false;
  mobileViewSubscription: Subscription;
  processingNumbers: Array<SmsProcessingPhoneNumberModel> = [];

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    private changeDetectorService: ChangeDetectorRef,
    public notificationService: NotificationService,
    public phoneNumbersService: PhoneNumbersService,
    public messagesDatasource: ProMessagesDatasource,
    public notificationsService: LocalNotificationsService,
    public zendeskService: ZendeskService,
    public dialog: MatDialog,
    public messagingService: ProMessagingService,
    private fb: UntypedFormBuilder,
    private router: Router,
  ) {
    super(route, headerService, titleService, authService);

    this.mobileViewSubscription = this.headerService.isMobileView.subscribe(value => {
      this.isMobileView = value;
      this.showThreads = !value;
      this.sidenavMode = value ? 'over' : 'side';
    });

    this.paramsSubscription = this.route.queryParams.subscribe((params: Params) => {
      this.onRouteParamsChange(params)
        .then()
        .catch(reason => {
          console.error(reason);
        });
    });

    this.deleteRoles = [RolesEnum.ProMessaging_Delete];
    this.editRoles = [RolesEnum.ProMessaging_Edit, RolesEnum.ProMessaging_Delete];

    this.createForm();
  }

  async onRouteParamsChange(params: Params) {
    console.log(`${this.TAG} onRouteParamsChange`, params);

    this.routedThreadKey = params['thread'];

    if (!this.routedPhoneNumber || this.routedPhoneNumber !== params['tn']) {
      this.routedPhoneNumber = params['tn'];

      await this.selectDefaultPhoneNumber();

      const messagingNumber = this.messagingService.selectedMessagingNumber;
      this.providerTypes = [];
      if (messagingNumber?.proEnabled) {
        this.providerTypes.push({
          value: 'text',
          title: 'SMS/MMS',
          class: 'fa-solid fa-comment-sms messaging-type-text',
        });
      }
      if (messagingNumber?.wabaDid) {
        this.providerTypes.push({
          value: 'waba',
          title: 'WhatsApp',
          class: 'fa-brands fa-whatsapp messaging-type-waba',
        });
      }

      if (messagingNumber && !messagingNumber.allowSend) {
        this.headerService.addGlobalStaticMessage({
          key: 'sms_read_only_warning',
          severity: 'warn',
          summary: 'View only access.',
          detail: 'To send a message, please contact your admin to update your role with View/Edit permission.',
        });
      } else {
        this.headerService.removeGlobalStaticMessage('sms_read_only_warning');
      }

      this.refreshProcessingNumbers().catch(reason => {
        console.error(reason);
      });
    }

    if (this.routedThreadKey == 'new') {
      this.messagingService.isComposing = true;
      this.selectedGroup = {
        groupKey: '',
        primaryNumber: '',
        unread: 0,
      };
      this.resetForm();
    } else {
      this.messagingService.isComposing = false;
    }

    this.updateCommunicationType(this.providerTypes[0]?.value || 'text');
  }

  async selectDefaultPhoneNumber() {
    if (this.routedPhoneNumber) {
      await this.messagingService.messagingPhoneNumbers(this.routedPhoneNumber, true);
    } else {
      await this.messagingService.messagingPhoneNumbers(undefined, true);

      const defaultMessagingNumber = this.messagingService.getMessagingNumber();
      if (defaultMessagingNumber) {
        this.navigateToThread(defaultMessagingNumber).catch(reason => {
          console.error(reason);
        });
      }
    }
  }

  async navigateToThread(tn: string, threadKey?: string) {
    console.log(`${this.TAG} navigateToThread`, tn, threadKey);
    await this.router.navigate([], {
      queryParams: {
        tn: tn,
        thread: threadKey,
      },
    });
  }

  onPhoneNumberValueChange(event: SmsPhoneNumberModel) {
    console.log(`${this.TAG} onPhoneNumberValueChange`, event);

    if (event?.campaignId == 'FAKE') {
      this.navigateToThread(undefined).catch(reason => {
        console.error(reason);
      });

      this.addNewSmsNumber();
    } else {
      this.navigateToThread(event.phoneNumber).catch(reason => {
        console.error(reason);
      });
    }
  }

  onPhoneNumberClicked(event: SmsPhoneNumberModel) {
    console.log(`${this.TAG} onPhoneNumberClicked`, event);
    this.messagingService.isComposing = false;

    if (event?.campaignId == 'FAKE') {
      this.navigateToThread(undefined).catch(reason => {
        console.error(reason);
      });

      this.addNewSmsNumber();
    } else {
      this.navigateToThread(event.phoneNumber).catch(reason => {
        console.error(reason);
      });
    }
  }

  onPhoneNumberOpenedChange(opened: boolean) {
    console.log(`${this.TAG} onPhoneNumberOpenedChange`, opened);
    if (!opened && this.routedPhoneNumber != this.messagingService.selectedMessagingNumber?.phoneNumber) {
      this.navigateToThread(this.messagingService.selectedMessagingNumber?.phoneNumber).catch(reason => {
        console.error(reason);
      });
    }
  }

  addNewSmsNumber() {
    const dialogRef = this.dialog.open(AddSmsNumberDialogComponent, {
      width: '600px',
      height: '350px',
      data: {
        smsNumberType: 'prosms',
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        await this.refreshProcessingNumbers();
        await this.selectDefaultPhoneNumber();
      }
    });
  }

  async refreshProcessingNumbers() {
    console.log('refreshProcessingNumbers');
    this.processingNumbers = await this.phoneNumbersService.getSmsProcessingPhoneNumbers({
      smsType: 'prosms',
    });

    if (this.processingNumbers.length > 0) {
      clearTimeout(this.refreshProcessingNumbersTimeout);
      setTimeout(async () => {
        await this.refreshProcessingNumbers();
      }, 15000);
    } else {
      await this.messagingService.messagingPhoneNumbers(this.routedPhoneNumber, false);
    }
  }

  onCurrentUserChanged(user: CurrentUser): void {
    if (user) {
      this.smsThreadSortType = user.uiSettings?.smsThreadSortType || 'sortByDateAndUnread';
      this.isAdmin = user.isAdmin;
      this.hasContactsModule = this.authService.userHasPackage([
        ModulesEnum.ContactModule,
        ModulesEnum.PrivateContactModule,
      ]);
      this.hasContactsViewRight = this.authService.hasAccess([
        RolesEnum.CompanyContact_View,
        RolesEnum.CompanyContact_Edit,
        RolesEnum.CompanyContact_Delete,
        RolesEnum.PrivateContact_View,
        RolesEnum.PrivateContact_Edit,
        RolesEnum.PrivateContact_Delete,
      ]);
      this.hasContactsEditRight = this.authService.hasAccess([
        RolesEnum.CompanyContact_Edit,
        RolesEnum.CompanyContact_Delete,
        RolesEnum.PrivateContact_Edit,
        RolesEnum.PrivateContact_Delete,
      ]);

      this.hasPhoneNumbersEditRight = this.authService.hasAccess([
        RolesEnum.PhoneNumbers_Edit,
        RolesEnum.PhoneNumbers_Delete,
      ]);

      this.hasAssignUserAccess = this.authService.hasAccess([
        RolesEnum.PhoneNumbers_Edit,
        RolesEnum.PhoneNumbers_Delete,
        RolesEnum.PhoneNumbers_AssignUser,
      ]);
      this.messagingService.setHasAssignUserAccess(this.hasAssignUserAccess);

      this.hasTemplateAccess = this.authService.hasAccess([
        RolesEnum.MessagingTemplateView,
        RolesEnum.MessagingTemplateEdit,
        RolesEnum.MessagingTemplateDelete,
      ]);

      if (user.isAdmin) {
        this.headerService.showInfoIcon = true;
        this.headerService.infoIconTooltip =
          'As an admin, you have access to view all SMS conversations on your account. You can change user permissions in the Admin Settings tab, or by contacting TeleGo Support.';
      }
    } else {
      this.isAdmin = false;
      this.hasContactsModule = false;
      this.hasContactsViewRight = false;
      this.hasContactsEditRight = false;
      this.hasPhoneNumbersEditRight = false;
      this.hasAssignUserAccess = false;
      this.hasTemplateAccess = false;

      this.messagingService.setHasAssignUserAccess(this.hasAssignUserAccess);

      this.headerService.showInfoIcon = false;
    }
  }

  ngOnInit() {
    super.ngOnInit();

    console.log('ProMessagingComponent ngOnInit');

    this.onDeleteConversationProcess = this.onDeleteConversationProcess.bind(this);

    this.newMessageSubscription = this.messagingService.onNewMessageSubject.subscribe(async message => {
      if (message && message.direction === 'out' && message.state === 'sent') {
        // get new billing limits
        await this.updateLimitsMessage();
      }
    });

    this.searchTextChanged.pipe(debounceTime(300), distinctUntilChanged()).subscribe(searchText => {
      this.searchText = searchText;
    });

    this.updateLimitsMessage().then();
  }

  onSelectedThreadChanged(thread: MessagingGroupModel) {
    console.log(`${this.TAG} onSelectedThreadChanged`, thread);
    this.storeTypedMessage();

    if (!this.routedThreadKey || this.routedThreadKey !== thread?.threadKey) {
      this.router
        .navigate([], { queryParams: { tn: this.routedPhoneNumber, thread: thread?.threadKey } })
        .catch(error => console.log(error));
    }

    this.updateMessagingForm(thread);
  }

  updateMessagingForm(thread: MessagingGroupModel) {
    console.log(`${this.TAG} updateMessagingForm`, thread);
    this.selectedGroup = thread;

    if (this.selectedGroup && this.selectedGroup.contacts) {
      const firstContact = this.selectedGroup.contacts[0];
      if (firstContact) {
        this.updateCommunicationType(firstContact.communicationType === 0 ? 'text' : 'waba');
      } else {
        this.updateCommunicationType('text');
      }
    }

    if (this.isMobileView) {
      this.threadsDrawer?.close().then();
    }

    this.resetForm();
  }

  async updateLimitsMessage() {
    console.log('updateLimitsMessage');
    this.messagingLimits = await this.messagingService.getMessagingLimits();
    if (this.messagingLimits.limits > 0) {
      this.limitsReached = this.messagingLimits.currentUsage >= this.messagingLimits.limits;

      const remainCount = this.messagingLimits.limits - this.messagingLimits.currentUsage;
      const limit10Percent = this.messagingLimits.limits * 0.1;

      if (limit10Percent >= remainCount) {
        this.headerService.addGlobalStaticMessage({
          key: 'pro_sms_limit_warning',
          severity: 'warn',
          summary: 'Warning!',
          detail: `You ${this.limitsReached ? 'have reached' : 'are approaching'} your monthly outgoing SMS message limit for your Pro SMS account.
          You have <b>${remainCount}</b> outgoing messages remaining out of <b>${this.messagingLimits.limits}</b>.
          <a href="javascript:void(0);" class="open-support"><b>Upgrade to Pro</b></a>`,
        });
        setTimeout(() => {
          const supportLink: HTMLAnchorElement = document
            .getElementsByClassName('open-support')
            .item(0) as HTMLAnchorElement;
          console.log('supportLink', supportLink);
          if (supportLink) {
            supportLink.onclick = () => {
              this.zendeskService.show();
              return false;
            };
          }
        });
      } else {
        this.headerService.removeGlobalStaticMessage('pro_sms_limit_warning');
        this.limitsReached = false;
      }
    } else {
      this.headerService.removeGlobalStaticMessage('pro_sms_limit_warning');
      this.limitsReached = false;
    }
  }

  storeTypedMessage() {
    if (this.selectedGroup?.threadKey && this.form.getRawValue().message) {
      const key = `${this.routedPhoneNumber}|${this.selectedGroup?.threadKey}`;
      this.uncompletedMessages[key] = this.form.getRawValue().message;
    }
    console.log(`${this.TAG} storeTypedMessage`, this.uncompletedMessages);
  }

  clearTypedMessage() {
    if (this.selectedGroup?.threadKey) {
      const key = `${this.messagingService.getMessagingNumber()}|${this.selectedGroup.groupKey}`;
      this.uncompletedMessages[key] = undefined;
    }
  }

  resetForm() {
    console.log(`${this.TAG} resetForm`, this.selectedGroup);
    const selectedGroup = this.selectedGroup;

    this.readOnlyPhone =
      (selectedGroup && !!selectedGroup.groupKey) || !this.messagingService.selectedMessagingNumber.allowSend;
    console.log(`${this.TAG} readOnlyPhone`, this.readOnlyPhone);
    this.removeAttachment();
    this.selectedTemplate = undefined;

    const clientNumbers: ContactTagLookupModel[] = [];
    let uncompletedMessage = '';
    if (selectedGroup && selectedGroup.contacts) {
      for (const contact of selectedGroup.contacts) {
        clientNumbers.push({
          phoneNumber: contact.phoneNumber || '',
          name: contact.name || '',
          type: 'Contacts',
          uniqueKey: `${contact.phoneNumber || ''}|Contacts||''}`,
          isOptedIn: contact.isOptedIn || false,
          communicationType: contact.communicationType || 0,
        });
      }
      const key = `${this.routedPhoneNumber}|${this.selectedGroup?.groupKey}`;
      uncompletedMessage = this.uncompletedMessages[key] || '';
      console.log(`${this.TAG} uncompletedMessage`, uncompletedMessage, key);
    }

    this.form.reset({
      primaryPhoneNumber: this.routedPhoneNumber,
      clientPhoneNumbers: clientNumbers,
      message: uncompletedMessage,
      type: this.communicationType || 'text',
    });
  }

  ngOnDestroy() {
    this.headerService.removeGlobalStaticMessage('sms_read_only_warning');
    this.headerService.removeGlobalStaticMessage('pro_sms_limit_warning');
    this.newMessageSubscription?.unsubscribe();
    this.paramsSubscription?.unsubscribe();
    this.staticMessagesSubscription?.unsubscribe();
    this.mobileViewSubscription?.unsubscribe();
    this.messagingService.messagingNumbers = [];
    this.headerService.showInfoIcon = false;
    clearTimeout(this.refreshProcessingNumbersTimeout);

    this.messagingService.isComposing = false;

    super.ngOnDestroy();
  }

  async markUnreadMessages() {
    console.log('mark unread messages');
    const group = this.selectedGroup;

    if ((group?.unread || 0) > 0) {
      this.messagingService.markUnreadMessages(group).catch(e => console.log(e));
    }
  }

  msgKeyPress(event) {
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();

      this.onSubmit().catch(error => console.error(error));
    }
  }

  onNewMessage(event) {
    event.preventDefault();

    this.messagingService.isComposing = true;

    this.navigateToThread(this.routedPhoneNumber, 'new').catch(reason => {
      console.error(reason);
    });
  }

  fileChangeEvent(fileInput: any) {
    const files = fileInput.target.files;
    if (files.length > 0) {
      const file: File = files[0];
      this.validateAndProceedFile(file);
      this.textInput.nativeElement.focus();
    }
  }

  onFilePaste(event: ClipboardEvent) {
    const clipboardItems = event.clipboardData?.items || [];
    for (let i = 0; i < clipboardItems.length; i++) {
      const item = clipboardItems[i];
      console.log('Item: ' + item.type);
      if (item.type.indexOf('image') > -1) {
        this.validateAndProceedFile(item.getAsFile() as File);
      } else {
        console.log('Discarding image paste data');
      }
    }
  }

  dropFile(event: DragEvent) {
    console.log('drop event');

    const files = event.dataTransfer?.files || [];

    if (files.length > 0) {
      const file: File = files[0];
      this.validateAndProceedFile(file);
    }
  }

  public validateAndProceedFile(file: File) {
    if (FileHelpers.isSupportedFile(file)) {
      if (FileHelpers.isSupported4MbFileSize(file)) {
        this.filesToUpload = [file];
        this.previewFile(file);
      } else {
        this.notificationService.notifyDanger('File size must be less then 3.75MB');
      }
    } else {
      this.notificationService.notifyDanger('File type is not supported');
    }
  }

  removeAttachment() {
    this.filesToUpload = [];
    this.previewUrl = undefined;
    this.previewFileName = undefined;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  async onSubmit() {
    console.log('form submit: ' + this.form.valid);
    if (this.form.valid && !this.submitting) {
      const reqData = this.prepareFormModel();
      if (reqData.type === 'waba' && (reqData.toBulk || []).length > 1) {
        this.dialog.open(MatAlertDialogComponent, {
          width: '500px',
          data: {
            title: 'Attention',
            actionTitle: 'Got It',
            text: 'You can send only individual message to WhatsApp client.',
            hideCancel: false,
          },
        });

        return;
      }

      if (reqData.type === 'waba' && !reqData.toBulk[0].isOptedIn) {
        this.dialog.open(MatAlertDialogComponent, {
          width: '500px',
          data: {
            title: 'Attention',
            actionTitle: 'Got It',
            text: 'You cannot send WhatsApp message, because contact is not opted in. Send welcome text message, requesting to reply with code: "whatsapp"',
            hideCancel: false,
          },
        });

        return;
      }

      //if (!this.readOnlyPhone) {
      if (reqData.toBulk.length > 1) {
        this.sendAsGroup = true;
        this.submitting = true;
        this.buttonSubmitPromise = this.processSubmit();
      } else {
        this.submitting = true;
        this.buttonSubmitPromise = this.processSubmit();
      }
      /*} else {
        this.submitting = true;
        this.buttonSubmitPromise = this.processSubmit();
      }*/
    }
  }

  async processSubmit(): Promise<void> {
    const reqData = this.prepareFormModel();

    if (!this.selectedTemplate && !reqData.text && !this.filesToUpload[0]) {
      this.submitting = false;
      this.notificationService.notifyDanger('Please enter message or select template or attach file');
      return;
    }

    if (reqData.type !== 'waba' && reqData.wabaDid) {
      reqData.wabaDid = undefined;
    }

    if (reqData.type === 'waba' && reqData.wabaDid && this.isConversationExpired()) {
      reqData.text = '';
    }

    if (this.selectedTemplate) {
      reqData.template = this.selectedTemplate;
    }

    try {
      const sentMessage = await this.messagingService.sendMessage(reqData, this.filesToUpload[0]);
      if (sentMessage) {
        //await this.threadCmp.updateThreadListAfterSendMessage(sentMessage);

        this.clearTypedMessage();
        this.messagingService.isComposing = false;
        this.resetForm();

        setTimeout(() => {
          this.navigateToThread(this.routedPhoneNumber, sentMessage.groupKey);
        });
      } else {
        this.notificationService.notifyDanger('Unable to send message');
      }
    } catch (e) {
      console.error('unable to send message: ', e);
    } finally {
      this.submitting = false;
    }
  }

  prepareFormModel(): SendProMessageModel {
    const formModel = this.form.value;

    return {
      from: formModel.primaryPhoneNumber,
      toBulk: formModel.clientPhoneNumbers,
      text: formModel.message,
      type: formModel.type,
      wabaDid: this.messagingService.selectedMessagingNumber?.wabaDid,
      sendAsGroup: /*this.readOnlyPhone ? */ formModel.clientPhoneNumbers.length > 1 /* : this.sendAsGroup,*/,
    };
  }

  createForm(): void {
    this.form = this.fb.group({
      primaryPhoneNumber: ['', Validators.required],
      searchText: [''],
      clientPhoneNumbers: [[], [Validators.required]],
      message: ['', [Validators.maxLength(2048)]],
      type: [this.communicationType, Validators.required],
    });
  }

  clearSearch($event: MouseEvent) {
    this.searchText = '';
    this.searchTextInternal = '';
    this.searchTextChanged.next('');
  }

  onSearchTextChange($event: any) {
    this.searchTextChanged.next($event);
  }

  private previewFile(file: File) {
    this.previewFileName = file.name;

    if (file.type.indexOf('image') > -1) {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        this.previewUrl = e.target.result;
        this.changeDetectorService.markForCheck();
      };

      reader.readAsDataURL(file);
    }
  }

  onTemplateSelected($event: any) {
    console.log($event);
    this.form.patchValue({
      message: $event,
    });
  }

  onDeleteClick() {
    if (!!this.selectedGroup.broadcastId) {
      const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
        // width: '250px',
        data: {
          title: 'Delete Conversation?',
          text: 'Are you sure you want to delete full conversation?',
          actionTitle: 'Delete',
        },
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.onDeleteConversationProcess(false).catch(error => console.error(error));
        }
      });
    } else {
      const dialogRef = this.dialog.open(ConfirmConversationDeleteDialogComponent);

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.onDeleteConversationProcess(result === 2).catch(error => console.error(error));
        }
      });
    }
  }

  async onDeleteConversationProcess(block: boolean): Promise<void> {
    await this.messagingService.deleteFullConversation(
      this.selectedGroup.groupKey,
      this.selectedGroup.broadcastId,
      block,
    );

    this.notificationService.notifySuccess('Conversation has been deleted');

    await this.navigateToThread(this.routedPhoneNumber);
  }

  isConversationExpired() {
    if (this.selectedGroup?.wabaConvExp) {
      const expDate = DateTime.fromISO(this.selectedGroup.wabaConvExp);
      const now = DateTime.local();

      return expDate < now;
    }

    return true;
  }

  canSendMessage() {
    // console.log('canSendMessage');
    if (this.form.getRawValue().type === 'waba') {
      // console.log('canSendMessage', this.lastMessage);
      const expDate = DateTime.fromISO(this.selectedGroup?.wabaConvExp);
      const now = DateTime.local();

      if (this.selectedGroup?.wabaConvId && this.lastMessage?.direction === 'in') {
        const messageTime = new Date(this.lastMessage.time);
        const timeDiff = DateTime.fromJSDate(messageTime).diffNow('hours');
        if (Math.abs(timeDiff.hours) < 24) {
          return true;
        }
      }

      if (expDate < now) {
        return false;
      } else {
        if (this.selectedGroup?.wabaConvId && this.lastMessage?.direction === 'out') {
          return false;
        }
      }

      return false;
    }

    return true;
  }

  onLastIncomingMessage($event: MessageModel) {
    console.log('onLastMessage');
    this.lastMessage = $event;
  }

  onSelectWhatsAppTemplate() {
    const dialogRef = this.dialog.open(SelectWhatsappTemplateDialogComponent, {
      width: '80%',
      height: '80%',
      data: {
        wabaId: this.messagingService.selectedMessagingNumber.wabaId,
      },
    });

    dialogRef.afterClosed().subscribe((result: { template: WhatsAppSendTemplateModel; file: File }) => {
      if (result) {
        console.log(result);
        this.selectedTemplate = result.template;
        if (result.file) {
          this.filesToUpload = [result.file];
        }
        this.onSubmit();
      }
    });
  }

  showAllNumbers(showAll: boolean) {
    this.messagingService.showAllNumbers(showAll);
  }

  clientPhoneNumbersChanged($event: ContactTagLookupModel[]) {
    console.log('clientPhoneNumbersChanged', $event);
    const firstContact = ($event || [])[0];
    if (firstContact) {
      const allPhoneNumbers = $event.map(x => x.phoneNumber);
      let groupNumbersArray: string[], groupKey: string;
      [groupNumbersArray, groupKey] = MessagingHelper.getGroupKeyUniqueKey(
        this.routedPhoneNumber,
        firstContact.phoneNumber,
        allPhoneNumbers,
        undefined,
      );

      this.selectCommunicationType(firstContact.communicationType === 0 ? 'text' : 'waba');

      this.navigateToThread(this.routedPhoneNumber, groupKey).catch(reason => {
        console.error(reason);
      });
    }
  }

  selectCommunicationType(type: 'text' | 'waba') {
    this.updateCommunicationType(type);
    this.form.patchValue({
      type: this.communicationType,
    });
  }

  updateCommunicationType(type: 'text' | 'waba') {
    console.log('updateCommunicationType', type);
    const availableType = this.providerTypes.find(x => x.value === type);
    if (availableType) {
      this.communicationType = type;
    } else {
      this.communicationType = this.providerTypes[0]?.value || 'text';
    }
  }

  onThreadsMenuOpened() {
    window.dispatchEvent(new Event('resize'));
  }

  openSupportWidget() {
    this.zendeskService.toggle();
  }
}
